import { action, observable, runInAction, makeObservable } from "mobx";
import { userInfoStore } from "Stores/UserInfoStore";
import { API } from "General/API";
import { handleStoreState, CoreStore } from "./RESTStore/CoreStore";
import merge from "deepmerge";
import { API_URL } from "General/APIEndpoints";
import { API_LINKEDUSERDETAILS_MAILSIGNATUREOPTION } from "General/APIEnums";

const emptyData: API_UserSettings = {
  password: undefined,
  sendMail: false,
  notAllowedToChangePassword: false,
  notAllowedToChangeSettings: false,
  sendMailCopyToMe: false,
  showMailSignature: false,
  mailSignature: undefined,
  language: "DE",
  layoutType: "SPLIT",
};
let unauthCounter = 0;

interface LoginContext {
  userId: number;
  unifiedAccount: boolean;
}

class UserSelfSettings implements API_UserSelfSettings {
  dashboardLayout?: API_USERSELFSETTINGS_DASHBOARDLAYOUT = "LIST";
  language?: API_USERSELFSETTINGS_LANGUAGE = "DE";
  layoutType?: API_USERSELFSETTINGS_LAYOUTTYPE = "SPLIT";
  mailSignature?: string = "";
  sendMailCopyToMe?: boolean = false;
  showMailSignature?: boolean = false;
  userid?: number = 0;
  constructor(data: API_UserSettings, userId: number) {
    Object.keys(this).forEach((key) => (this[key] = data[key]));
    this.userid = userId;
  }
}

class UserSettingsStore extends CoreStore {
  data: API_UserSettings = JSON.parse(JSON.stringify(emptyData)) as API_UserSettings;
  userDetails: API_LinkedUserDetails[] = null;
  saveSettingsState: StoreState = "RAW";
  private currentUserId: number;

  constructor(options) {
    super(options);

    makeObservable(this, {
      data: observable,
      userDetails: observable,
      saveSettingsState: observable,
      clear: action.bound,
      getSettings: action.bound,
      getSettingsIfNew: action.bound,
      update: action.bound,
      fetch: action.bound,
      saveSettings: action.bound,
      setLayoutType: action.bound,
      fetchUserDetails: action.bound,
    });
  }

  private getLoginContext(): LoginContext | null {
    const { id, unifiedAccount, status } = userInfoStore.loginInfo;
    const userId = Number(id);

    if (status !== "OK" || !Number.isFinite(userId) || userId <= 0) {
      return null;
    }

    return {
      userId,
      unifiedAccount: Boolean(unifiedAccount),
    };
  }

  private isSameLoginContext(context: LoginContext): boolean {
    const currentContext = this.getLoginContext();

    return (
      !!currentContext &&
      currentContext.userId === context.userId &&
      currentContext.unifiedAccount === context.unifiedAccount
    );
  }

  private getSettingsUrl(context: LoginContext): string {
    if (context.unifiedAccount) {
      return API_URL.PAVE_USERS.getPAVEUserSettings(context.userId);
    }
    return API_URL.USERS.getUserSettings(context.userId);
  }

  url() {
    if (userInfoStore.loginInfo.unifiedAccount) {
      return API_URL.PAVE_USERS.getPAVEUserSettings(userInfoStore.loginInfo.id);
    }
    return API_URL.USERS.getUserSettings(userInfoStore.loginInfo.id);
  }

  clear() {
    this.data = JSON.parse(JSON.stringify(emptyData));
  }

  @handleStoreState()
  @handleStoreState<UserSettingsStore>("saveSettingsState")
  async fetch() {
    try {
      const loginContext = this.getLoginContext();
      if (!loginContext) {
        return;
      }

      const response: API_UserSettings = await API.getData(this.getSettingsUrl(loginContext));
      if (!this.isSameLoginContext(loginContext)) {
        return;
      }
      runInAction(() => {
        this.data = response;
      });
      if (loginContext.unifiedAccount) {
        await this.fetchUserDetails();
      }
    } catch (error) {
      /**
       * Considering that this request is the first request that is made before fetching other data
       * (src/Config/Router.tsx).
       * Therefore we handle jwtToken feature here - use jwtToken to get valid csrf token and resume rendering process.
       * Otherwise - logout.
       */
      if (error.response && error.response.status === 401 && userInfoStore.jwtToken) {
        if (unauthCounter > 1) {
          console.log("unauthCounter = ", unauthCounter);

          throw error;
        }
        ++unauthCounter;
        const loginResponse = await API.newData(API_URL.LOGIN.loginUserMain(), {});
        if (loginResponse.token) {
          userInfoStore.authenticate(loginResponse);
        }
        await this.fetch();
      } else {
        throw error;
      }
    }
  }

  async fetchSilently() {
    const loginContext = this.getLoginContext();
    if (!loginContext) {
      return;
    }

    const response: API_UserSettings = await API.getData(this.getSettingsUrl(loginContext));
    if (!this.isSameLoginContext(loginContext)) {
      return;
    }

    runInAction(() => {
      this.data = response;
    });
    if (loginContext.unifiedAccount) {
      await this.fetchUserDetails();
    }
  }

  @handleStoreState<UserSettingsStore>("state")
  async getSettings() {
    try {
      await this.fetch();
    } catch (err) {
      throw err;
    }
  }

  async fetchUserDetails() {
    const response = await API.getData(API_URL.PAVE_USERS.getLinkedUserDetails());
    runInAction(() => {
      this.userDetails = response;
    });
  }

  /**
   * Fetches User Settings if users ID differs
   */
  async getSettingsIfNew() {
    const loginContext = this.getLoginContext();
    if (!loginContext) {
      return;
    }

    if (this.currentUserId === loginContext.userId) {
      return;
    }
    this.currentUserId = loginContext.userId;
    return await this.getSettings();
  }

  @handleStoreState<UserSettingsStore>("saveSettingsState")
  async saveSettings() {
    try {
      const loginContext = this.getLoginContext();
      if (!loginContext) {
        return;
      }

      let url: string;
      if (loginContext.unifiedAccount) {
        url = API_URL.PAVE_USERS.updatePAVEUserSettings(loginContext.userId);
      } else {
        url = API_URL.USERS.updateUserSettings(loginContext.userId);
      }
      await API.editData(url, new UserSelfSettings(this.data, loginContext.userId));
      userInfoStore.setLanguage(this.data.language.toLowerCase());
      this.fetch();
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async setLayoutType(key: API_USERDATA_LAYOUTTYPE) {
    this.data.layoutType = key;
    return await this.saveSettings();
  }

  update(newData: API_UserSettings) {
    this.data = merge(JSON.parse(JSON.stringify(this.data)), JSON.parse(JSON.stringify(newData)), {
      arrayMerge: (destinationArray, sourceArray) => sourceArray,
    });
  }

  getEmailSignature(projectId: number): { signature: string; showSignature: boolean } {
    if (userInfoStore.loginInfo.unifiedAccount) {
      const userDetails = this.userDetails.find((entry) =>
        entry.assignedProjects.some((project) => project.id === projectId)
      );
      if (userDetails) {
        if (userDetails.mailSignatureOption === API_LINKEDUSERDETAILS_MAILSIGNATUREOPTION.UNIFIED) {
          return {
            signature: this.data.mailSignature,
            showSignature: this.data.showMailSignature,
          };
        } else if (userDetails.mailSignatureOption === API_LINKEDUSERDETAILS_MAILSIGNATUREOPTION.CUSTOM) {
          return {
            signature: userDetails.mailSignature,
            showSignature: true,
          };
        }
      }
      // mailSignatureOption == NONE or no userDetails found
      return {
        signature: null,
        showSignature: false,
      };
    }
    return {
      signature: this.data.mailSignature,
      showSignature: this.data.showMailSignature,
    };
  }
}

const userSettingsStore = new UserSettingsStore({ omitAllStoresArray: true, id: "id" });

export { userSettingsStore };
